import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Tabs, Tab } from "react-bootstrap";
import { formatDuration, formatDateTime, getHtmlReportLink, FileGuard } from "../../utils/utils";
import RenderTable from "../Tables/RenderTable";
import ExcelViewer from "../Tables/ExcelViewer";
import AuspiceViewer from "../Tables/AuspiceViewer"; // your component
import NwkHtmlViewer from "../Tables/NwkHtmlViewer";


import {
  StatusBadgeRenderer,
  cardStyle,
  cardHeaderStyle,
  iframeStyle,
  cardContentWrapper,
  jobDetailContainerStyle,
  jobDetailHeaderStyle,
  errorsContainerStyle,
  errorsListStyle,
  htmlReportLinkStyle,
  noDataStyle,
} from "../../styles/theme";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

const JobDetail = ({ job }) => {
  if (!job) return null;

  const [jobFiles, setJobFiles] = useState({ inputs: [], outputs: [] });


  const generalRows = [
    { label: "Job Tag", value: job.job_tag },
    { label: "Job Type", value: job.pipeline_name },
    { label: "Status", value: <StatusBadgeRenderer value={job.status} /> },
    { label: "Duration", value: formatDuration(job.duration) },
    { label: "Start Time", value: formatDateTime(job.start_time) },
    { label: "End Time", value: formatDateTime(job.end_time) },
    { label: "Unique Job ID", value: job.unique_job_id },
  ];

  const fetchJobDetails = useCallback(async (jobId) => {
    if (!jobId) return;

    try {
      const res = await axios.get(`${API_BASE_URL}/api/jobs/details`, { params: { jobId } });
      setJobFiles(res.data);
    } catch (err) {
      console.error("Error fetching job inputs:", err);
      setJobFiles({ inputObj: [], outputObj: [], errors: [] });
    }
  }, []);

  useEffect(() => {
    if (job?.id) fetchJobDetails(job.id);
    else ssetJobFiles({ inputObj: [], outputObj: [], errors: [] });
  }, [job, fetchJobDetails]);

  
  // ✅ Collect NWK/NH + matching HTML pairs
  const treePairs = [];

  if (jobFiles.outputObj && jobFiles.outputObj.length > 0) {
    jobFiles.outputObj.forEach(file => {
      const ext = file.file_name.split(".").pop().toLowerCase();

      if (["nwk", "nh"].includes(ext)) {
        const baseName = file.file_name.replace(/\.[^/.]+$/, "");

        const matchingHtml = jobFiles.outputObj.find(f => {
          const fExt = f.file_name.split(".").pop().toLowerCase();
          const fBase = f.file_name.replace(/\.[^/.]+$/, "");
          return fExt === "html" && fBase === baseName;
        });

        treePairs.push({
          name: baseName,
          nwk: file,
          html: matchingHtml
        });
      }
    });
  }



  return (
    <div style={jobDetailContainerStyle} >
      <h5 style={jobDetailHeaderStyle}>Job Details</h5>

      <Tabs defaultActiveKey="general" id="job-detail-tabs" className="mb-3">

        {/* General Tab */}
        <Tab eventKey="general" title="General">
          <RenderTable rows={generalRows} />
        </Tab>

        {/* Inputs Tab */}
        <Tab eventKey="inputs" title="Inputs">
          {jobFiles.inputObj && (
            <>
              <RenderTable
                rows={[
                  ...(jobFiles.inputObj.baseSpaceId
                    ? jobFiles.inputObj.baseSpaceId.split(";").map((id, index) => ({
                        label: index === 0 ? "BaseSpace ID" : "", // only first row gets label
                        value: id.trim(),
                      }))
                    : [{ label: "BaseSpace ID", value: "-" }]
                  ),
                  ...(jobFiles.inputObj.file_name
                    ? [{ label: "Metadata", value: jobFiles.inputObj.file_name }]
                    : [])
                ]}
              />
              {jobFiles.inputObj.file_path && (
                <div className="mt-3">
                  <ExcelViewer
                    filePath={`${API_BASE_URL}/data/${jobFiles.inputObj.file_path}`}
                    maxHeight={500}
                  />
                </div>
              )}
            </>
          )}
        </Tab>

       
        {/* Outputs Tab */}
        <Tab eventKey="outputs" title="Outputs">
          {jobFiles.outputObj && jobFiles.outputObj.length > 0 ? (

            <>


              {/* ✅ Render Other Files Normally */}
              {jobFiles.outputObj.map((output, i) => {
                const fileExt = output.file_name.split(".").pop().toLowerCase();
                const baseName = output.file_name.replace(/\.[^/.]+$/, "");

                // Skip NWK/NH (already handled in NwkHtmlViewer)
                if (["nwk", "nh"].includes(fileExt)) return null;

                // Skip HTML paired with NWK/NH
                const isPairedHtml = treePairs.some(tree => tree.name === baseName);
                if (fileExt === "html" && isPairedHtml) return null;

                let content = null;
                const fileUrl = `${API_BASE_URL}/data/${output.file_path}`;

                if (["xlsx", "csv", "tsv","txt"].includes(fileExt)) {
                  content = (
                    <FileGuard filePath={fileUrl}>
                        <ExcelViewer filePath={fileUrl} maxHeight={500} />
                    </FileGuard>
                  );
                } else if (fileExt === "html") {
                  content = ( 
                      <FileGuard filePath={fileUrl}>
                        {getHtmlReportLink(output.file_name, fileUrl, htmlReportLinkStyle )}
                      </FileGuard>
                  )
                } else if (fileExt === "json") {
                  content = (
                     <FileGuard filePath={fileUrl}>
                      <a href={fileUrl} target="_blank" rel="noopener noreferrer">
                        {output.file_name}
                      </a>
                    </FileGuard>
                  );
                } else {
                  content = (
                    <FileGuard filePath={fileUrl}>
                      <a href={fileUrl} target="_blank" rel="noopener noreferrer">
                        {output.file_name}
                      </a>
                    </FileGuard>
                  );
                }

                return (
                  <div key={i} style={cardStyle}>
                    <h6 style={cardHeaderStyle}>{output.file_name}</h6>
                    <div style={cardContentWrapper}>{content}</div>
                  </div>
                );
              })}


              {/* ✅ ONE Tree Viewer Section */}
              {treePairs.length > 0 && (
                <div style={cardStyle}>
                  <h6 style={cardHeaderStyle}>Phylogenetic Trees</h6>
                  <div style={cardContentWrapper}>
                    <NwkHtmlViewer trees={treePairs} />
                  </div>
                </div>
              )}
              
            </>

          ) : (
            <div style={noDataStyle}>No outputs found</div>
          )}
        </Tab>



        {/* Errors Tab */}
        <Tab eventKey="errors" title="Errors">
          <div style={errorsContainerStyle}>
            {jobFiles.errors && jobFiles.errors.length > 0 ? (
              <ul style={errorsListStyle}>
                {jobFiles.errors.map((err, i) => <li key={i}>{err}</li>)}
              </ul>
            ) : (
              <div style={noDataStyle}>No errors</div>
            )}
          </div>
        </Tab>

      </Tabs>
    </div>
  );
};

export default JobDetail;
